import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Package } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { StatusTimeline } from '../components/OrderHistoryModal';
import { PaymentProofCard } from '../components/order/PaymentProofCard';
import { EditOrderCard } from '../components/order/EditOrderCard';
import { OrderChat } from '../components/order/OrderChat';
import { money, unitLocal, orderTotals } from '../lib/orderMoney';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Summary = ({ o }) => {
  const t = orderTotals(o);
  return (
    <div data-testid="portal-summary" className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4">
      <h3 className="text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Items</h3>
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {(o.items || []).map((it, i) => (
          <div key={i} className="flex justify-between gap-3 py-2 text-[13px]">
            <span className="min-w-0 truncate">{it.qty} × {it.name} <span className="text-neutral-500">{it.strength}</span></span>
            <span className="shrink-0">{money(o, unitLocal(o, it.price) * it.qty)}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-100 dark:border-neutral-800 text-[13px] space-y-1">
        <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{money(o, t.subtotal)}</span></div>
        {Number(t.discount) > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>- {money(o, t.discount)}</span></div>}
        <div className="flex justify-between"><span className="text-neutral-500">Shipping</span><span>{money(o, t.shipping)}</span></div>
        <div className="flex justify-between font-semibold"><span>Total</span><span data-testid="portal-total">{money(o, t.total)}</span></div>
        <div className="text-[11.5px] text-neutral-500">Payment method: {o.network}</div>
      </div>
    </div>
  );
};

export default function OrderPortalPage() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { headers } = useCustomerAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const urlToken = sp.get('t') || '';

  const load = useCallback(() => axios.get(`${API}/order-portal/${id}`, { params: { t: urlToken }, headers })
    .then(({ data: d }) => { setData(d); setErr(''); })
    .catch(() => setErr('We couldn\'t open this order. Use the link from your order email, or sign in with the email you ordered with.')), [id, urlToken, headers]);
  useEffect(() => { load(); const h = setInterval(load, 20000); return () => clearInterval(h); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (err) return (
    <div data-testid="portal-error" className="max-w-xl mx-auto px-6 py-24 text-center">
      <Package size={36} className="mx-auto mb-3 text-neutral-400" />
      <p className="text-[14px] text-neutral-600 dark:text-neutral-300">{err}</p>
      <Link to="/" className="inline-block mt-5 text-brand font-semibold text-[13.5px]">Back to shop</Link>
    </div>
  );
  if (!data) return <div className="py-24 text-center text-neutral-500 text-[13.5px]">Loading your order…</div>;

  const o = data.order;
  const token = o.access_token || urlToken;
  const send = async (text) => { await axios.post(`${API}/order-portal/${id}/messages`, { t: token, text }, { headers }); await load(); };
  return (
    <main data-testid="order-portal-page" className="max-w-6xl mx-auto px-6 py-10 page-fade-in">
      <Link to="/" className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-brand"><ArrowLeft size={13} /> Back to shop</Link>
      <div className="mt-3 mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-brand font-semibold">Your order</div>
        <h1 data-testid="portal-order-id" className="font-serif text-4xl sm:text-5xl text-neutral-900 dark:text-neutral-100 mt-1">{o.id}</h1>
      </div>
      <StatusTimeline order={o} />
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 items-start">
        <div className="space-y-6">
          <Summary o={o} />
          <EditOrderCard order={o} requests={data.edit_requests} canEdit={data.can_edit} token={token} headers={headers} onDone={load} />
        </div>
        <div className="space-y-6">
          <PaymentProofCard order={o} proofs={data.proofs} token={token} headers={headers} onDone={load} />
          <OrderChat messages={data.messages} onSend={send} me="customer" title="Message us about this order" hint="We reply by email too" testPrefix="portal-chat" />
        </div>
      </div>
    </main>
  );
}
